import { useState } from 'react';

interface Example {
  problem: string;
  steps: string[];
}

interface PracticeQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface Lesson {
  id: number;
  title: string;
  vocabulary: { term: string; definition: string }[];
  conceptExplanation: string;
  examples: Example[];
  practiceQuestions: PracticeQuestion[];
  teacherTip: string;
}

interface LessonCardProps {
  lesson: Lesson;
}

export default function LessonCard({ lesson }: LessonCardProps) {
  const [expandedExamples, setExpandedExamples] = useState<Record<number, number>>({});
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);

  const revealNextStep = (exampleIndex: number) => {
    setExpandedExamples(prev => ({
      ...prev,
      [exampleIndex]: (prev[exampleIndex] || 0) + 1
    }));
  };

  const resetExample = (exampleIndex: number) => {
    setExpandedExamples(prev => ({
      ...prev,
      [exampleIndex]: 0
    }));
  };

  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    if (!showResults) {
      setSelectedAnswers(prev => ({
        ...prev,
        [questionIndex]: answerIndex
      }));
    }
  };

  const checkAnswers = () => {
    setShowResults(true);
  };

  const resetPractice = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };

  const calculateScore = () => {
    let correct = 0;
    lesson.practiceQuestions.forEach((q, index) => {
      if (selectedAnswers[index] === q.correctAnswer) {
        correct++;
      }
    });
    return correct;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
      <h3 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-lg text-white font-bold">
          {lesson.id}
        </div>
        {lesson.title}
      </h3>

      {/* Vocabulary Section */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <i className="ri-book-open-line text-orange-500"></i>
          Key Vocabulary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lesson.vocabulary.map((item, index) => (
            <div key={index} className="bg-orange-50 rounded-lg p-4">
              <span className="font-bold text-orange-700">{item.term}:</span>
              <span className="text-gray-700 ml-2">{item.definition}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Concept Explanation */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <i className="ri-lightbulb-line text-orange-500"></i>
          Understanding the Concept
        </h4>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-6">
          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{lesson.conceptExplanation}</p>
        </div>
      </div>

      {/* Worked Examples */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <i className="ri-pencil-line text-orange-500"></i>
          Worked Examples
        </h4>
        <div className="space-y-6">
          {lesson.examples.map((example, exampleIndex) => (
            <div key={exampleIndex} className="bg-gray-50 rounded-lg p-6">
              <p className="font-bold text-lg text-gray-900 mb-4">Example {exampleIndex + 1}: {example.problem}</p>
              <div className="space-y-3 mb-4">
                {example.steps.map((step, stepIndex) => (
                  <div
                    key={stepIndex}
                    className={`pl-4 border-l-4 border-orange-500 py-2 transition-all duration-300 ${
                      stepIndex < (expandedExamples[exampleIndex] || 0)
                        ? 'opacity-100'
                        : 'opacity-0 h-0 overflow-hidden'
                    }`}
                  >
                    <p className="text-gray-700">{step}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                {(expandedExamples[exampleIndex] || 0) < example.steps.length && (
                  <button
                    onClick={() => revealNextStep(exampleIndex)}
                    className="bg-orange-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Reveal Next Step
                  </button>
                )}
                {(expandedExamples[exampleIndex] || 0) > 0 && (
                  <button
                    onClick={() => resetExample(exampleIndex)}
                    className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-400 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Practice Questions */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <i className="ri-question-line text-orange-500"></i>
          Practice Questions
        </h4>
        <div className="space-y-6">
          {lesson.practiceQuestions.map((question, qIndex) => (
            <div key={qIndex} className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-6">
              <p className="font-bold text-lg text-gray-900 mb-4">
                Question {qIndex + 1}: {question.question}
              </p>
              <div className="space-y-3 mb-4">
                {question.options.map((option, oIndex) => {
                  const isSelected = selectedAnswers[qIndex] === oIndex;
                  const isCorrect = oIndex === question.correctAnswer;
                  const showCorrectness = showResults && isSelected;

                  return (
                    <button
                      key={oIndex}
                      onClick={() => handleAnswerSelect(qIndex, oIndex)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        isSelected
                          ? showCorrectness
                            ? isCorrect
                              ? 'border-green-500 bg-green-50'
                              : 'border-red-500 bg-red-50'
                            : 'border-orange-500 bg-orange-100'
                          : 'border-gray-200 hover:border-orange-300 bg-white'
                      }`}
                    >
                      <span className="font-semibold text-gray-700">{String.fromCharCode(65 + oIndex)}.</span>{' '}
                      <span className="text-gray-800">{option}</span>
                      {showResults && isSelected && (
                        <span className="ml-2">
                          {isCorrect ? '✓' : '✗'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {showResults && selectedAnswers[qIndex] !== undefined && (
                <div className={`p-4 rounded-lg ${
                  selectedAnswers[qIndex] === question.correctAnswer
                    ? 'bg-green-100 border-2 border-green-500'
                    : 'bg-blue-50 border-2 border-blue-500'
                }`}>
                  <p className="text-gray-800">
                    <strong>Explanation:</strong> {question.explanation}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-4 mt-6">
          {!showResults && Object.keys(selectedAnswers).length === lesson.practiceQuestions.length && (
            <button
              onClick={checkAnswers}
              className="bg-orange-500 text-white px-8 py-3 rounded-lg font-bold hover:bg-orange-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Check My Answers
            </button>
          )}
          {showResults && (
            <>
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-3 rounded-lg font-bold">
                Your Score: {calculateScore()} / {lesson.practiceQuestions.length}
              </div>
              <button
                onClick={resetPractice}
                className="bg-gray-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>

      {/* Teacher's Tip */}
      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-l-4 border-amber-500 rounded-lg p-6">
        <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
          <i className="ri-star-line text-amber-500"></i>
          Teacher's Tip from Mr. Augustine
        </h4>
        <p className="text-gray-700 leading-relaxed">{lesson.teacherTip}</p>
      </div>
    </div>
  );
}